/**
 * The subtitle strip under the player (1.9.0), shown only while the Subtitles tab is open: a window around the
 * playhead (20 s by default, zoom 5 s to 5 min, following playback) with a timecode ruler, the waveform and labelled
 * cue segments. Clicking a cue selects it and seeks; dragging an edge trims it and dragging the body moves it, with
 * contracts setCueEdge / moveCue (frame snapping, neighbour clamps), one draft change per drag.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { TICKS_PER_SECOND, cueRuns, effectiveCueStyle, cueRow, runsText, type Cue, type CueEditContext, type Rational, type TrackStyle } from '@prism/contracts';
import { cueTimecode } from './timecode';
import { cueSeverities, dragCues, followWindow, hitPart, nextZoom, rulerMarks, rulerStep, ticksToX, xToTicks, zoomWindow, type StripPart } from './stripMath';

export const STRIP_DEFAULT_SECONDS = 20;
export const STRIP_MIN_SECONDS = 5;
export const STRIP_MAX_SECONDS = 300;

export interface SubtitleStripProps {
  cues: Cue[];
  trackStyle: TrackStyle;
  rate: Rational;
  /** The timecode at media time 0, in frames (the ruler's labels). */
  startFrames: number;
  durationTicks: number;
  /** The playhead in ticks (read from the video element on each frame, not the throttled playerTime). */
  playheadTicks: number;
  selectedIds: string[];
  /** The window's length in seconds (STRIP_MIN_SECONDS–STRIP_MAX_SECONDS). */
  windowSeconds: number;
  onWindowSeconds(seconds: number): void;
  onSeek(ticks: number): void;
  onSelect(id: string, opts: { extend: boolean }): void;
  /** The cues after one drag. */
  onCues(next: Cue[]): void;
  waveformUrl: string | null;
  waveformKey: string | null;
  readOnly?: boolean;
  testId?: string;
}

interface Waveform { peaks: number[]; length: number }
interface Drag { id: string; part: StripPart; x0: number; moved: boolean; extend: boolean; pointer: number }

const ROWS = ['t', 'm', 'b'] as const;

export function SubtitleStrip({ cues, trackStyle, rate, startFrames, durationTicks, playheadTicks, selectedIds, windowSeconds, onWindowSeconds, onSeek, onSelect, onCues, waveformUrl, waveformKey, readOnly, testId = 'subtitle-strip' }: SubtitleStripProps) {
  const lane = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const [wave, setWave] = useState<Waveform | null>(null);
  const windowTicks = Math.max(1, Math.round(Math.min(STRIP_MAX_SECONDS, Math.max(STRIP_MIN_SECONDS, windowSeconds)) * TICKS_PER_SECOND));
  const [start, setStart] = useState(() => followWindow(0, playheadTicks, windowTicks, durationTicks));
  const [drag, setDrag] = useState<(Drag & { dx: number }) | null>(null);
  // The worst issue per cue (amber marker for a warning, red for an error); computed over the whole track for overlaps.
  const severities = useMemo(() => cueSeverities(cues, trackStyle, durationTicks), [cues, trackStyle, durationTicks]);
  const ctx: CueEditContext = useMemo(() => ({ frameRate: rate, durationTicks: durationTicks || null, trackStyle }), [rate, durationTicks, trackStyle]);

  useEffect(() => { if (!drag) setStart((s) => followWindow(s, playheadTicks, windowTicks, durationTicks)); }, [playheadTicks, windowTicks, durationTicks, drag]);
  useEffect(() => {
    const el = lane.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  useEffect(() => {
    if (!waveformUrl) { setWave(null); return; }
    let cancelled = false;
    fetch(waveformUrl).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!cancelled) setWave(d); }).catch(() => undefined);
    return () => { cancelled = true; };
    // Keyed by representation: a new token in the URL does not fetch again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveformKey]);

  // The waveform of the window.
  useEffect(() => {
    const c = canvas.current;
    if (!c || !width) return;
    const dpr = window.devicePixelRatio || 1;
    const h = c.clientHeight || 40;
    c.width = width * dpr;
    c.height = h * dpr;
    const g = c.getContext('2d');
    if (!g) return;
    g.scale(dpr, dpr);
    g.clearRect(0, 0, width, h);
    if (!wave?.length || !durationTicks) {
      g.fillStyle = 'rgba(255,255,255,.06)';
      for (let x = 0; x < width; x += 6) g.fillRect(x, h / 2 - 0.5, 3, 1);
      return;
    }
    let max = 1;
    for (const p of wave.peaks) max = Math.max(max, Math.abs(p));
    const scale = (h / 2 - 2) / max;
    g.fillStyle = 'rgba(165,151,255,.45)';
    for (let x = 0; x < width; x++) {
      const t0 = xToTicks(x, start, windowTicks, width), t1 = xToTicks(x + 1, start, windowTicks, width);
      const i0 = Math.max(0, Math.floor((t0 / durationTicks) * wave.length));
      const i1 = Math.min(wave.length, Math.max(i0 + 1, Math.floor((t1 / durationTicks) * wave.length)));
      let lo = 0, hi = 0;
      for (let i = i0; i < i1; i++) { lo = Math.min(lo, wave.peaks[i * 2] ?? 0); hi = Math.max(hi, wave.peaks[i * 2 + 1] ?? 0); }
      g.fillRect(x, h / 2 - hi * scale, 1, Math.max(1, (hi - lo) * scale));
    }
  }, [wave, width, start, windowTicks, durationTicks]);

  // The cues as a drag leaves them (shown while dragging; one change on release).
  const shown = useMemo(() => (drag && drag.moved ? dragCues(cues, drag.id, drag.part, drag.dx, windowTicks, width, ctx) : cues), [cues, drag, windowTicks, width, ctx]);
  const rows = useMemo(() => {
    const used = new Set(shown.map((c) => cueRow(effectiveCueStyle(trackStyle, c).align)));
    return ROWS.filter((r) => used.has(r));
  }, [shown, trackStyle]);
  const end = start + windowTicks;
  const visible = shown.filter((c) => c.endTicks > start && c.startTicks < end);
  const x = (t: number) => ticksToX(t, start, windowTicks, width);
  const step = rulerStep(windowTicks / TICKS_PER_SECOND, width || 600);
  const marks = rulerMarks(start, windowTicks, step);
  const selected = new Set(selectedIds);

  const localX = (e: { clientX: number }) => e.clientX - (lane.current?.getBoundingClientRect().left ?? 0);
  const press = (e: RPointerEvent<HTMLDivElement>, c: Cue) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const px = localX(e);
    const part = readOnly ? 'body' : hitPart(px, x(c.startTicks), x(c.endTicks), selected.has(c.id) && selectedIds.length === 1);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ id: c.id, part, x0: px, dx: 0, moved: false, extend: e.shiftKey || e.metaKey || e.ctrlKey, pointer: e.pointerId });
  };
  const move = (e: RPointerEvent<HTMLDivElement>) => {
    if (!drag || readOnly) return;
    const dx = localX(e) - drag.x0;
    if (!drag.moved && Math.abs(dx) < 3) return;
    setDrag({ ...drag, dx, moved: true });
  };
  const release = (e: RPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const d = drag;
    setDrag(null);
    if (d.moved) {
      const next = dragCues(cues, d.id, d.part, d.dx, windowTicks, width, ctx);
      if (next !== cues) onCues(next);
      if (!selected.has(d.id)) onSelect(d.id, { extend: false });
      return;
    }
    const c = cues.find((k) => k.id === d.id);
    onSelect(d.id, { extend: d.extend });
    if (c && !d.extend) onSeek(c.startTicks);
  };

  const zoom = (dir: 1 | -1) => {
    const next = nextZoom(windowTicks / TICKS_PER_SECOND, dir, STRIP_MIN_SECONDS, STRIP_MAX_SECONDS);
    setStart((s) => zoomWindow(s, playheadTicks, windowTicks, Math.round(next * TICKS_PER_SECOND), durationTicks));
    onWindowSeconds(next);
  };
  const label = (s: number) => (s >= 60 ? `${s / 60} min` : `${s} s`);
  const seconds = windowTicks / TICKS_PER_SECOND;

  return (
    <div className="subtitle-strip" data-testid={testId} data-window={seconds} data-start={start}>
      <div className="strip-bar">
        <span className="strip-title">Subtitles</span>
        <span className="faint mono strip-range">{cueTimecode(start, rate, startFrames)} – {cueTimecode(Math.min(end, durationTicks || end), rate, startFrames)}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn ghost icon sm" aria-label="Zoom out" title="Show more time" disabled={seconds >= STRIP_MAX_SECONDS} onClick={() => zoom(-1)} data-testid="strip-zoom-out"><ZoomOut size={14} /></button>
        <span className="mono faint strip-window" data-testid="strip-window">{label(seconds)}</span>
        <button type="button" className="btn ghost icon sm" aria-label="Zoom in" title="Show less time" disabled={seconds <= STRIP_MIN_SECONDS} onClick={() => zoom(1)} data-testid="strip-zoom-in"><ZoomIn size={14} /></button>
      </div>
      <div className="strip-lane" ref={lane} onPointerDown={(e) => { if (e.button === 0) onSeek(Math.round(xToTicks(localX(e), start, windowTicks, width))); }}
        onPointerMove={move} onPointerUp={release} onPointerCancel={() => setDrag(null)}>
        <div className="strip-ruler" aria-hidden>
          {marks.map((t) => <span key={t} className="strip-mark" style={{ left: x(t) }}>{cueTimecode(t, rate, startFrames)}</span>)}
        </div>
        <canvas ref={canvas} className="strip-wave" aria-hidden />
        <div className="strip-cues" style={{ ['--rows' as string]: Math.max(1, rows.length) }}>
          {visible.map((c) => {
            const severity = severities.get(c.id);
            const row = Math.max(0, rows.indexOf(cueRow(effectiveCueStyle(trackStyle, c).align)));
            const x0 = x(c.startTicks), x1 = x(c.endTicks);
            const on = selected.has(c.id);
            const text = runsText(cueRuns(c.text)).replace(/\n/g, ' ');
            return (
              <div key={c.id} className={`strip-cue ${on ? 'selected' : ''} ${severity ?? ''} ${drag?.id === c.id && drag.moved ? 'dragging' : ''}`} data-cue={c.id} data-testid="strip-cue" data-severity={severity}
                style={{ left: x0, width: Math.max(3, x1 - x0), top: `calc(${row} * 100% / var(--rows))`, height: `calc(100% / var(--rows) - 4px)` }}
                title={text} onPointerDown={(e) => press(e, c)}>
                {severity && <span className={`strip-cue-marker ${severity}`} aria-hidden />}
                {on && !readOnly && selectedIds.length === 1 && <span className="strip-handle start" data-testid="strip-handle-start" aria-hidden />}
                <span className="strip-cue-label" dir="auto">{text || '…'}</span>
                {on && !readOnly && selectedIds.length === 1 && <span className="strip-handle end" data-testid="strip-handle-end" aria-hidden />}
              </div>
            );
          })}
        </div>
        {durationTicks > 0 && durationTicks < end && <div className="strip-past-end" style={{ left: x(durationTicks) }} title="After the end of the media" aria-hidden />}
        <div className="strip-playhead" style={{ left: x(playheadTicks) }} data-testid="strip-playhead" aria-hidden />
      </div>
    </div>
  );
}
